import { X } from "lucide-react";
import { MedicineApiResponse, Order } from "../types";
import { useState, useRef, useEffect } from "react";
import MedicationRow, { Medication } from "./MedicationRow";
import transparentBg from "../../../../../../assets/images/transparent-bg.png";
import defaultAvatar from "../../../../../../assets/images/default_avatar.jpg";
import WithoutPrescriptionMedicineOrderDetails from "./WithoutPrescriptionMedicineOrderDetails";
import { AdminOrderService } from "../../../../../../services/api/AdminOrderService/admin.order.service";
import toast from "react-hot-toast";

interface Props {
  setShowGeneratePrescriptionForm: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setPrescriptionImageData: React.Dispatch<
    React.SetStateAction<{
      image: string | null;
      formData: PrescriptionFormData | null;
    }>
  >;
  onSavePrescription?: () => void;
  selectedOrder: Order | null;
  patientId?: string;
}

export interface PrescriptionFormData {
  userDetails: {
    name: string;
    phone: string;
    avatar?: string;
  };
  chiefComplaints: string[];
  diagnosis: string[];
  medications: Medication[];
}

interface PatientDetails {
  name: string;
  mobile_number: string;
  profile_image?: string;
}

const GeneratePrescriptionForm: React.FC<Props> = ({
  setShowGeneratePrescriptionForm,
  setPrescriptionImageData,
  onSavePrescription,
  selectedOrder,
  patientId,
}) => {
  const formRef = useRef<HTMLDivElement>(null);
  const [chiefComplaints, setChiefComplaints] = useState<string[]>([]);
  const [currentComplaint, setCurrentComplaint] = useState("");
  const [diagnosis, setDiagnosis] = useState<string[]>([]);
  const [currentDiagnosis, setCurrentDiagnosis] = useState("");
  const [patientDetails, setPatientDetails] = useState<PatientDetails | null>(
    null
  );

  const [medicationInput, setMedicationInput] = useState({
    name: "",
    dosage: 1,
    details: "",
    duration: 1,
    salt:""
  });
  const [medications, setMedications] = useState<Medication[]>([
   
  ]);

  const handleGenerateImage = async () => {
    if (!formRef.current || !selectedOrder) {
      toast.error("Form reference or order missing");
      return;
    }

    try {
      // Create the form data object first
      const formData: PrescriptionFormData = {
        userDetails: {
          name: patientDetails?.name || "",
          phone: patientDetails?.mobile_number || "",
          avatar: patientDetails?.profile_image,
        },
        chiefComplaints: [...chiefComplaints],
        diagnosis: [...diagnosis],
        medications: [...medications],
      };

      // Update the parent state with the form data
      setPrescriptionImageData((prev) => ({
        ...prev,
        formData: formData,
      }));

      // Close the form
      setShowGeneratePrescriptionForm(false);

      // Call any additional save handler
      if (onSavePrescription) {
        onSavePrescription();
      }

      toast.success("Prescription generated successfully");
    } catch (error) {
      console.error("Failed to generate prescription:", error);
      toast.error("Failed to generate prescription. Please try again.");
    }
  };


  const addMedication = () => {
    if (!medicationInput.name.trim()) {
      toast.error("Medicine name is required");
      return;
    }

    const newMedication = {
      ...medicationInput,
      id:
        medications.length > 0
          ? Math.max(...medications.map((m) => m.id)) + 1
          : 1,
    };

    setMedications((prev) => [...prev, newMedication]);
    setMedicationInput({
      name: "",
      dosage: 1,
      details: "",
      duration: 1,
      salt:""
    });
    toast.success("Medicine added successfully");
  };


  const removeMedication = (id: number) => {
    setMedications((prev) => prev.filter((med) => med.id !== id));
  };

  const updateMedication = (id: number, updated: Partial<Medication>) => {
    setMedications((prev) =>
      prev.map((med) => (med.id === id ? { ...med, ...updated } : med))
    );
  };

  const handleComplaintChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCurrentComplaint(e.target.value);
    // Split by new lines to create array of complaints
    const complaints = e.target.value.split("\n").filter((line) => line.trim());
    setChiefComplaints(complaints);
  };

  const handleDiagnosisChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCurrentDiagnosis(e.target.value);
    // Split by new lines to create array of diagnoses
    const diagnoses = e.target.value.split("\n").filter((line) => line.trim());
    setDiagnosis(diagnoses);
  };

  useEffect(() => {
    if (!patientId) return;

    const getPatientDetails = async () => {
      try {
        const response = await AdminOrderService.getAdminPatientDetailsById(
          patientId
        );
        setPatientDetails(response.data);
      } catch (error) {
        console.log(error);
      }
    };

    getPatientDetails();
  }, [patientId]);

  return (
    <>
      <section className="absolute inset-0 z-50 min-h-screen">
        <div
          className="absolute inset-0 bg-cover bg-center flex justify-center px-2 pb-2 overflow-hidden overflow-y-auto"
          style={{ backgroundImage: `url(${transparentBg})` }}
        >
          <div
            ref={formRef}
            className="w-[750px] m-auto bg-white rounded-lg shadow-none p-6"
            style={{ overflow: "visible", maxHeight: "none" }}
          >
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-green-600 font-semibold mb-4">
                  PRESCRIPTION FORM
                </h2>
              </div>
              <div>
                <X
                  className="w-5 h-5 text-red-600 cursor-pointer exclude-from-capture"
                  onClick={() => setShowGeneratePrescriptionForm(false)}
                />
              </div>
            </div>

            <div className="flex items-center gap-4 mb-6">
              <img
                src={
                  patientDetails?.profile_image
                    ? patientDetails.profile_image
                    : defaultAvatar
                }
                alt="profile"
                className="w-14 h-14 rounded-full"
              />
              <div>
                <h3 className="font-bold text-lg">{patientDetails?.name}</h3>
                <p className="text-gray-500 text-sm">
                  {patientDetails?.mobile_number}
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm text-[#003861] font-medium mb-1">
                Chief Complaints
              </label>
              <textarea
                className="border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-500 w-full"
                placeholder="Enter complaints (one per line)"
                value={currentComplaint}
                onChange={handleComplaintChange}
                rows={3}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm text-[#003861] font-medium mb-1">
                Diagnosis/Provisional Diagnosis
              </label>
              <textarea
                className="border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-500 w-full"
                placeholder="Enter diagnoses (one per line)"
                value={currentDiagnosis}
                onChange={handleDiagnosisChange}
                rows={3}
              />
            </div>

            <h3 className="text-md text-[#003861] font-semibold mb-2">
              Medication Prescribed
            </h3>
            <div className="bg-gray-50 rounded-lg p-3 mb-3">
              <MedicationRow
                medication={medicationInput}
                onRemove={() => {}}
                onUpdate={(updated) =>
                  setMedicationInput((prev) => ({ ...prev, ...updated }))
                }
                isInputRow={true}
              />
              <button
                onClick={addMedication}
                className="mt-3 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Add Medicine
              </button>
            </div>

            <h3 className="text-md text-[#003861] font-semibold mb-2">
              Added Medicine list
            </h3>
            {/* List of Added Medications */}
            <div className="max-h-[200px] overflow-y-auto space-y-3">
              {medications.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  No medications added yet
                </p>
              ) : (
                medications.map((medication) => (
                  <div
                    key={medication.id}
                    className="bg-gray-50 rounded-lg p-3 relative"
                  >
                    <MedicationRow
                      medication={medication}
                      onRemove={() => removeMedication(medication.id)}
                      onUpdate={(updated) =>
                        updateMedication(medication.id, updated)
                      }
                      isInputRow={false}
                    />
                  </div>
                ))
              )}
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                className="px-4 py-2 rounded bg-gray-200 exclude-from-capture"
                onClick={() => setShowGeneratePrescriptionForm(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-green-600 text-white exclude-from-capture"
                onClick={handleGenerateImage}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </section>

      <WithoutPrescriptionMedicineOrderDetails
        setShowGeneratePrescriptionForm={setShowGeneratePrescriptionForm}
        selectedOrder={selectedOrder}
      />
    </>
  );
};

export default GeneratePrescriptionForm;
